import { ContentReportService } from '@/service'
import { reactive } from 'vue'
import { PostState } from './posts'

export enum ContentType {
  POST = 'POST',
  COMMENT = 'COMMENT',
  MEMBER = 'MEMBER',
}

export class ContentReportsState {
  state = reactive({
    fetching: false,
    error: null as any,
    reportedContent: [] as any[],
    reportedContentPages: new Set(),
    nextPage: true,
  })

  async fetchReportedContent(groupId: string, locationId: string, query?: any) {
    try {
      let newReportedContent = []

      //Reset post if when fetching first page
      if (!query.previousId) {
        this.state.reportedContent = []
        this.state.reportedContentPages = new Set()
        this.state.nextPage = true
      }

      //Page already fetched
      if (
        query.previousId &&
        this.state.reportedContentPages.has(query.previousId)
      ) {
        return
      }

      //nextPage Not available
      if (!this.state.nextPage) {
        return
      }

      this.state.fetching = true
      newReportedContent = await ContentReportService.getReportedContent(
        groupId,
        locationId,
        query
      )
      await this.populateContent(newReportedContent)
      this.state.reportedContent.push(...newReportedContent)
      //To avoid repeative fetching of same page
      if (query.previousId) {
        this.state.reportedContentPages.add(query.previousId)
      }

      //Check if nextPage is available
      if (query.limit && newReportedContent.length < query.limit) {
        this.state.nextPage = false
      }
    } catch (error: any) {
      this.state.error = error?.response?.message || 'Something went wrong'
      console.error(error)
      throw error
    } finally {
      setTimeout(() => (this.state.fetching = false), 1000)
    }
  }

  async fetchReportedComment(commentId: string) {
    const reportedComment = await ContentReportService.getReportedComment(
      commentId
    )

    return reportedComment
  }

  async keepReportedContent(reportedContent) {
    // call service to remove from DB
    await ContentReportService.keepReportedContent(reportedContent._id)
    // Remove reported content from state (this.reportedContent)
    this.state.reportedContent = this.state.reportedContent.filter(
      content => content._id !== reportedContent._id
    )
  }

  async removeReportedContent(reportedContent) {
    // call service to remove from DB [Both reportedContent and POST]
    await ContentReportService.removeReportedContent(
      reportedContent._id,
      reportedContent.contentData?.postId || reportedContent.contentData?._id
    )
    // Remove reported content from state (this.reportedContent)
    this.state.reportedContent = this.state.reportedContent.filter(
      content => content._id !== reportedContent._id
    )
  }

  async populateContent(reportedContent) {
    // Fetch post/comment data for reported content, concurrently
    await Promise.all(
      reportedContent.map(async reportedRecord => {
        if (reportedRecord.contentType === ContentType.POST) {
          try {
            const post = await PostState.fetchPost(reportedRecord.contentId)
            reportedRecord.contentData = post
          } catch (error: any) {
            console.error(error)
          }
        }

        if (reportedRecord.contentType === ContentType.COMMENT) {
          try {
            const commentPromise = this.fetchReportedComment(
              reportedRecord.contentId
            )
            const comment = await commentPromise
            reportedRecord.contentData = comment
          } catch (error: any) {
            console.error(error)
          }
        }
      })
    )
  }
}
export async function reportContent(
  contentId: string,
  contentType: ContentType,
  groupId: string,
  locationId: string,
  contactId: string,
  _fullName: string,
  parentPostId?: string
) {
  // Call service to report the content
  const response = await ContentReportService.reportContent({
    contentId,
    contentType,
    groupId,
    locationId,
    reportedBy: contactId,
    parentPostId,
  })
  return response
}

export const ReportedContentState = new ContentReportsState()
